package fr.frux.feature.treedetail.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.ShoppingBasket
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.StatusTile
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_observation_empty
import fr.frux.feature.treedetail.resources.treedetail_observation_label
import fr.frux.feature.treedetail.resources.treedetail_observation_latest
import fr.frux.feature.treedetail.resources.treedetail_observation_option_flowering
import fr.frux.feature.treedetail.resources.treedetail_observation_option_picked
import fr.frux.feature.treedetail.resources.treedetail_observation_option_ripe
import fr.frux.feature.treedetail.resources.treedetail_observation_stale
import fr.frux.feature.treedetail.resources.treedetail_observation_status_long_flowering
import fr.frux.feature.treedetail.resources.treedetail_observation_status_long_picked
import fr.frux.feature.treedetail.resources.treedetail_observation_status_long_ripe
import fr.frux.feature.treedetail.resources.treedetail_observation_tile_description
import fr.frux.feature.treedetail.resources.treedetail_observation_title
import fr.frux.shared.domain.model.ObservableTreeStatus
import fr.frux.shared.domain.model.TreeObservation
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/** Préfixe du `testTag` des tuiles d'observation, suivi du nom de l'[ObservableTreeStatus]. */
internal const val OBSERVATION_TILE_TAG_PREFIX = "observationTile_"

private val OBSERVATION_OPTIONS =
    listOf(
        ObservableTreeStatus.FLOWERING,
        ObservableTreeStatus.RIPE_READY,
        ObservableTreeStatus.PICKED_CLEAN,
    )

/**
 * Bloc « Sur le terrain » : trois tuiles pour dire où en est l'arbre, puis la dernière observation
 * connue. La tuile de la dernière observation est cochée tant qu'elle n'est pas périmée (#648),
 * et chaque appui envoie une observation, y compris sur le statut déjà observé (ADR 0021,
 * re-observer rafraîchit la date). Mêmes icônes et libellés que l'étape 3 de l'assistant d'ajout.
 */
@Composable
internal fun FieldObservationSection(
    latestObservation: TreeObservation?,
    statusStale: Boolean,
    isPending: Boolean,
    ripeGlyph: ImageVector,
    onObserve: (ObservableTreeStatus) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier =
            modifier
                .fillMaxWidth()
                .background(MaterialTheme.colorScheme.surfaceVariant, RoundedCornerShape(20.dp))
                .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(
                text = stringResource(Res.string.treedetail_observation_label).uppercase(),
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(
                text = stringResource(Res.string.treedetail_observation_title),
                style = MaterialTheme.typography.titleLarge,
                color = MaterialTheme.colorScheme.onSurface,
            )
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OBSERVATION_OPTIONS.forEach { status ->
                val look = status.look(ripeGlyph)
                val description = stringResource(Res.string.treedetail_observation_tile_description, look.label)
                StatusTile(
                    label = look.label,
                    icon = look.icon,
                    iconContainer = look.container,
                    iconTint = look.tint,
                    selected = !statusStale && latestObservation?.status == status,
                    onToggle = { onObserve(status) },
                    enabled = !isPending,
                    modifier =
                        Modifier
                            .weight(1f)
                            .testTag(OBSERVATION_TILE_TAG_PREFIX + status.name)
                            .semantics { contentDescription = description },
                )
            }
        }
        LatestObservationText(latestObservation, statusStale)
    }
}

@Composable
private fun LatestObservationText(
    latestObservation: TreeObservation?,
    statusStale: Boolean,
) {
    if (latestObservation == null) {
        Text(
            text = stringResource(Res.string.treedetail_observation_empty),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        return
    }
    Text(
        text =
            stringResource(
                Res.string.treedetail_observation_latest,
                latestObservation.status.longLabel(),
                latestObservation.observedAtMillis.relativeAgeLabel(),
            ),
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
    if (statusStale) {
        Text(
            text = stringResource(Res.string.treedetail_observation_stale),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f),
        )
    }
}

private class TileLook(
    val label: String,
    val icon: ImageVector,
    val container: Color,
    val tint: Color,
)

@Composable
private fun ObservableTreeStatus.look(ripeGlyph: ImageVector): TileLook {
    val scheme = MaterialTheme.colorScheme
    val extra = MaterialTheme.fruxColors
    return when (this) {
        ObservableTreeStatus.FLOWERING -> {
            TileLook(
                stringResource(Res.string.treedetail_observation_option_flowering),
                FruxSpeciesGlyphs.Fleur,
                scheme.tertiaryContainer,
                scheme.onTertiaryContainer,
            )
        }

        ObservableTreeStatus.RIPE_READY -> {
            TileLook(
                stringResource(Res.string.treedetail_observation_option_ripe),
                ripeGlyph,
                extra.accentLeafContainer,
                extra.statusAvailable,
            )
        }

        ObservableTreeStatus.PICKED_CLEAN -> {
            TileLook(
                stringResource(Res.string.treedetail_observation_option_picked),
                Icons.Outlined.ShoppingBasket,
                scheme.surfaceVariant,
                scheme.onSurfaceVariant,
            )
        }
    }
}

/** Forme longue en minuscule, pour la ligne « Dernière observation : prêt à cueillir, il y a 2 j ». */
@Composable
private fun ObservableTreeStatus.longLabel(): String =
    when (this) {
        ObservableTreeStatus.FLOWERING -> stringResource(Res.string.treedetail_observation_status_long_flowering)
        ObservableTreeStatus.RIPE_READY -> stringResource(Res.string.treedetail_observation_status_long_ripe)
        ObservableTreeStatus.PICKED_CLEAN -> stringResource(Res.string.treedetail_observation_status_long_picked)
    }

/** Délègue au formateur partagé (#420), seul vocabulaire de fraîcheur de l'app. */
@OptIn(ExperimentalTime::class)
private fun Long.relativeAgeLabel(): String =
    fr.frux.shared.presentation
        .relativeAgeLabel(this, Clock.System.now().toEpochMilliseconds())
